import { api } from '../api/client';

// ─────────────────────────────────────────────────────────────────────────────
// WAITING FOR A DOCUMENT IMPORT, WITH AN END (Carla, live, 2026-10-21).
//
// Projects → Import from document with her PDF: 202, then the status was polled ~45 times and the
// screen never finished or said why, while the job itself had ENDED ('error' after 91 s, with its
// reason). The old loop lived inside the importer and had one way to stop that did not need an
// answer: a 2.5-minute check made BETWEEN polls. A status request that never answered held the
// loop where that check could not run, and a closed importer polled on behind the screen.
//
// Now every way out is bounded and every outcome is one of three, each with a sentence:
//   done      the plan
//   error     the job ended in error: the SERVER's reason, word for word
//   gave_up   this screen stopped waiting after LIMIT_MS (said plainly; nothing was saved)
// Each status request is cut off at REQUEST_TIMEOUT_MS, or sooner if the overall limit comes first,
// so no single request can outlive the limit. `signal` stops it (the importer closed).
// It never throws: a failed poll is not a failed import, and a thrown error in the caller's catch
// would be described as one.
// ─────────────────────────────────────────────────────────────────────────────

export const POLL_INTERVAL_MS = 2000;
// The server ends a job at 2 minutes (routes/ai/importDocument.js JOB_DEADLINE_MS) and calls a job
// still 'processing' at 3 minutes dead; 2.5 minutes here is after the first and before the second.
export const LIMIT_MS = 150000;
export const REQUEST_TIMEOUT_MS = 15000;

export const GAVE_UP_REASON = 'Aidey stopped waiting for this import after two and a half minutes, '
  + 'and nothing was saved. Try again, or import a shorter document or a section of it.';

const sleep = (ms, signal) => new Promise((resolve) => {
  const t = setTimeout(resolve, ms);
  if (signal) signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
});

/**
 * @returns {Promise<{status:'done', plan:object}|{status:'error'|'gave_up', reason:string}|{status:'stopped'}>}
 */
export async function awaitImportJob(jobId, { signal, intervalMs = POLL_INTERVAL_MS, limitMs = LIMIT_MS, requestTimeoutMs = REQUEST_TIMEOUT_MS } = {}) {
  // The timings are injectable so the tests run the real control flow without waiting minutes.
  const started = Date.now();
  const left = () => limitMs - (Date.now() - started);
  while (true) {                                                    // eslint-disable-line no-constant-condition
    if (signal?.aborted) return { status: 'stopped' };
    await sleep(Math.min(intervalMs, Math.max(0, left())), signal);
    if (signal?.aborted) return { status: 'stopped' };
    if (left() <= 0) return { status: 'gave_up', reason: GAVE_UP_REASON };

    const ctl = new AbortController();
    const stop = () => ctl.abort();
    if (signal) signal.addEventListener('abort', stop, { once: true });
    const cut = setTimeout(stop, Math.max(1, Math.min(requestTimeoutMs, left())));
    let data = null;
    try {
      const res = await api(`/api/ai/import-document/status/${encodeURIComponent(jobId)}`, { signal: ctl.signal });
      data = await res.json();
    } catch (err) {
      // An expired session does not fix itself by waiting (api() has already said so).
      if (err?.isAuthError) return { status: 'gave_up', reason: 'You were signed out before this import finished. Sign back in and import it again.' };
      // A job this screen cannot find will not appear by waiting either.
      if (err?.status === 404) return { status: 'error', reason: 'Aidey could not find this import any more, and nothing was saved. Please import the file again.' };
      data = null; // a transient failure, or a request cut off: keep waiting, inside the limit
    } finally {
      clearTimeout(cut);
      if (signal) signal.removeEventListener('abort', stop);
    }
    if (signal?.aborted) return { status: 'stopped' };
    if (data?.status === 'done') return { status: 'done', plan: data.plan };
    if (data?.status === 'error') return { status: 'error', reason: data.error || 'The import did not say why.' };
    // 'processing', or no answer: go round again.
  }
}
